import { useCallback, useMemo, useState } from 'react';

import { useShareTrainingUsers } from '../../../../redux/api/trainingsAPI';
import { useGetUserTrainingAccessIncoming } from '../../../../redux/api/userTrainingAccessApi';
import { useGetMyDetails } from '../../../../redux/api/users/usersApi';
import { ITraining } from '../../../../types/interfaces/training';
import { IUser } from '../../../../types/interfaces/user';
import { orderBy } from '../../../../utils/collections/array-utils';
import { getUserViewName } from '../../../../utils/common/shared-util';
import {
  accessRowToIUser,
  computeIdDelta,
} from '../../../users/settings/access-settings/access-settings-helpers';

export function useTrainingShareAccess(
  training: ITraining | undefined,
  trainingId: string,
) {
  const { data: user } = useGetMyDetails();
  const {
    data: userTrainingAccess,
    isLoading: isAccessLoading,
    isFetching: isAccessFetching,
  } = useGetUserTrainingAccessIncoming();
  const [shareTrainingUsers, { isLoading: isPosting }] =
    useShareTrainingUsers();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<IUser | null>(null);

  const shareTrainingAllowedUsers = useMemo(
    () =>
      (userTrainingAccess?.shareTrainings ?? []).map((row) =>
        accessRowToIUser(row),
      ),
    [userTrainingAccess?.shareTrainings],
  );

  const modalUsers = useMemo(() => {
    let base = shareTrainingAllowedUsers.map((u) => ({ ...u }));
    const existing = training?.availableToUsers ?? [];
    for (const u of existing) {
      if (u.id !== user?.id && !base.some((b) => b.id === u.id)) {
        base = [{ ...u }, ...base];
      }
    }
    const withName = base.map((u) => ({
      ...u,
      displayName: getUserViewName(u),
    }));
    return orderBy(withName, ['displayName'], ['asc']).filter(
      (u) => u.id !== user?.id,
    );
  }, [shareTrainingAllowedUsers, training?.availableToUsers, user?.id]);

  const selectedUserIds = useMemo(
    () => (training?.availableToUsers ?? []).map((u) => u.id),
    [training?.availableToUsers],
  );

  const displayUsers = useMemo(() => {
    const list = training?.availableToUsers ?? [];
    const withName = list.map((u) => ({
      ...u,
      displayName: getUserViewName(u),
    }));
    return orderBy(withName, ['displayName'], ['asc']);
  }, [training?.availableToUsers]);

  const handleModalSave = useCallback(
    async (nextIds: string[]) => {
      const prevIds = (training?.availableToUsers ?? []).map((u) => u.id);
      const { add, remove } = computeIdDelta(prevIds, nextIds);
      if (add.length === 0 && remove.length === 0) {
        setDialogOpen(false);
        return;
      }
      try {
        await shareTrainingUsers({
          trainingId,
          add,
          remove,
        }).unwrap();
        setDialogOpen(false);
      } catch {
        // RTK toast
      }
    },
    [training?.availableToUsers, trainingId, shareTrainingUsers],
  );

  const handleConfirmRemove = useCallback(async () => {
    if (!removeTarget) {
      return;
    }
    try {
      await shareTrainingUsers({
        trainingId,
        add: [],
        remove: [removeTarget.id],
      }).unwrap();
      setRemoveTarget(null);
    } catch {
      // RTK toast
    }
  }, [removeTarget, shareTrainingUsers, trainingId]);

  return {
    displayUsers,
    modalUsers,
    selectedUserIds,
    dialogOpen,
    setDialogOpen,
    removeTarget,
    setRemoveTarget,
    handleModalSave,
    handleConfirmRemove,
    isPosting,
    isAccessLoading,
    isAccessFetching,
  };
}
